import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Activity, 
  Clock, 
  FileText, 
  Plus, 
  Edit, 
  Trash2,
  Loader2,
  User,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface UserActivityLogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    id: string;
    email: string;
    full_name?: string | null;
    profiles?: {
      full_name: string | null;
    };
  } | null;
}

interface ActivityEntry {
  id: string;
  table_name: string;
  operation: string;
  record_id: string | null;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  changed_fields: string[] | null;
  created_at: string;
}

export function UserActivityLog({ open, onOpenChange, user }: UserActivityLogProps) {
  const { data: activities, isLoading } = useQuery({
    queryKey: ["user-activity", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await api
        .from("audit_logs")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as ActivityEntry[];
    },
    enabled: open && !!user?.id,
  });

  const getOperationIcon = (operation: string) => {
    switch (operation) {
      case "INSERT":
        return <Plus className="w-4 h-4 text-green-500" />;
      case "UPDATE":
        return <Edit className="w-4 h-4 text-blue-500" />;
      case "DELETE":
        return <Trash2 className="w-4 h-4 text-red-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getOperationBadge = (operation: string) => {
    const config: Record<string, { label: string; color: string }> = {
      INSERT: { label: "Creó", color: "bg-green-500/10 text-green-500" },
      UPDATE: { label: "Editó", color: "bg-blue-500/10 text-blue-500" },
      DELETE: { label: "Eliminó", color: "bg-red-500/10 text-red-500" },
    };
    const { label, color } = config[operation] || { label: operation, color: "bg-gray-500/10 text-gray-500" };
    return <Badge className={color}>{label}</Badge>;
  };

  const getTableLabel = (table: string) => {
    const labels: Record<string, string> = {
      work_orders: "Orden de Trabajo",
      profiles: "Perfil de Usuario",
      user_roles: "Rol de Usuario",
      users: "Usuario",
      molds: "Molde",
      machines: "Máquina",
      customers: "Cliente",
      process_records: "Registro de Proceso",
      production_batches: "Lote de Producción",
      quality_inspections: "Inspección de Calidad",
      material_inventory: "Materiales",
      raw_materials: "Materia Prima",
      product_models: "Modelo de Producto",
      news: "Noticia",
      links: "Enlace",
      albums: "Álbum",
      tools: "Herramienta",
    };
    return labels[table] || table;
  };

  const getTableIcon = (table: string) => {
    // Por simplicidad, usamos FileText para todos
    return <FileText className="w-4 h-4 text-muted-foreground" />;
  };

  const formatChanges = (activity: ActivityEntry) => {
    if (!activity.changed_fields || activity.changed_fields.length === 0) {
      return null;
    }

    const relevantFields = activity.changed_fields.filter(
      (f) => !["updated_at", "created_at"].includes(f)
    );

    if (relevantFields.length === 0) return null;

    return (
      <span className="text-xs text-muted-foreground">
        Campos: {relevantFields.slice(0, 3).join(", ")}
        {relevantFields.length > 3 && ` +${relevantFields.length - 3} más`}
      </span>
    );
  };

  const userName = user?.profiles?.full_name || user?.full_name || user?.email;

  // Agrupar actividades por fecha
  const groupedActivities = activities?.reduce((acc, activity) => {
    const date = format(new Date(activity.created_at), "yyyy-MM-dd");
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(activity);
    return acc;
  }, {} as Record<string, ActivityEntry[]>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Actividad de {userName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : activities && activities.length > 0 ? (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              {Object.entries(groupedActivities || {}).map(([date, dayActivities]) => (
                <div key={date}>
                  <div className="sticky top-0 bg-background py-2 z-10">
                    <Badge variant="outline" className="font-normal">
                      {format(new Date(date), "EEEE, d 'de' MMMM", { locale: es })}
                    </Badge>
                  </div>
                  <div className="space-y-3 mt-3">
                    {dayActivities.map((activity) => (
                      <div
                        key={activity.id}
                        className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      >
                        <div className="p-2 rounded-full bg-muted">
                          {getOperationIcon(activity.operation)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {getOperationBadge(activity.operation)}
                            <span className="font-medium">
                              {getTableLabel(activity.table_name)}
                            </span>
                          </div>
                          {formatChanges(activity)}
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {format(new Date(activity.created_at), "HH:mm:ss", { locale: es })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Activity className="w-12 h-12 mb-4 opacity-50" />
            <p>No hay actividad registrada para este usuario</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}





